import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as invoicesApi from './invoices.api';

import type { SupplierChangeApprovalListQuery, SupplierChangeApprovalRow } from './invoices.api';

import { extractServerError } from '@/lib/api-error';

/**
 * React Query hooks for the invoice supplier-change approval queue (ADMIN
 * only). Mirrors `use-void-approvals.ts`.
 */

const extractMessage = (err: unknown): string | null => extractServerError(err).message;

export const SUPPLIER_CHANGE_APPROVALS_QK = {
  all: () => ['invoices', 'supplier-change-approvals'] as const,
  list: (query: SupplierChangeApprovalListQuery) =>
    ['invoices', 'supplier-change-approvals', 'list', query] as const,
  pendingCount: () => ['invoices', 'supplier-change-approvals', 'pending-count'] as const,
};

export const useSupplierChangeApprovals = (query: SupplierChangeApprovalListQuery = {}) =>
  useQuery({
    queryKey: SUPPLIER_CHANGE_APPROVALS_QK.list(query),
    queryFn: () => invoicesApi.listSupplierChangeApprovals(query),
    staleTime: 15_000,
  });

export const useSupplierChangeApprovalsPendingCount = (enabled = true) =>
  useQuery({
    queryKey: SUPPLIER_CHANGE_APPROVALS_QK.pendingCount(),
    queryFn: () => invoicesApi.supplierChangeApprovalsPendingCount(),
    enabled,
    refetchInterval: 60_000,
    retry: false,
    throwOnError: false,
  });

const invalidateAfterDecision = (
  qc: ReturnType<typeof useQueryClient>,
  invoiceId?: string,
): void => {
  void qc.invalidateQueries({ queryKey: SUPPLIER_CHANGE_APPROVALS_QK.all() });
  void qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
  void qc.invalidateQueries({ queryKey: ['invoices', 'supplier-refunds'] });
  if (invoiceId) {
    void qc.invalidateQueries({ queryKey: ['invoices', 'detail', invoiceId] });
    void qc.invalidateQueries({ queryKey: ['invoices', 'edit-data', invoiceId] });
  }
};

/** Approve a pending request - executes the supplier change server-side. */
export const useApproveSupplierChange = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }): Promise<SupplierChangeApprovalRow> =>
      invoicesApi.approveSupplierChange(id),
    onSuccess: (row) => {
      invalidateAfterDecision(qc, row.invoiceId);
      toast.success('Supplier change approved');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to approve supplier change'),
  });
};

/** Reject a pending request. A remark is required. */
export const useRejectSupplierChange = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      remark,
    }: {
      id: string;
      remark: string;
    }): Promise<SupplierChangeApprovalRow> => invoicesApi.rejectSupplierChange(id, { remark }),
    onSuccess: (row) => {
      invalidateAfterDecision(qc, row.invoiceId);
      toast.success('Supplier change rejected');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to reject supplier change'),
  });
};

/** Approve every pending request in a request group. */
export const useBulkApproveSupplierChange = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestGroupId }: { requestGroupId: string }): Promise<{ approved: number }> =>
      invoicesApi.bulkApproveSupplierChange(requestGroupId),
    onSuccess: (res) => {
      invalidateAfterDecision(qc);
      void qc.invalidateQueries({ queryKey: ['invoices', 'detail'] });
      void qc.invalidateQueries({ queryKey: ['invoices', 'edit-data'] });
      toast.success(`${res.approved} supplier change(s) approved`);
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to approve supplier changes'),
  });
};
